import { ProgressiveHydration, registerComponent, useMatchMobile } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import GeneralPrice from 'nordicnest/product/Price';
import * as React from 'react';
import SvenssonsPrice from 'svenssons/product/Price';
import { ButtonBlock } from 'webapp/blocks/ButtonBlock';
import Link from 'webapp/components/Link';
import { Plus } from 'webapp/components/icons';
import Image from 'webapp/components/image';
import { Block, desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { decorators } from 'webapp/html-decorators/fontSizeDecorator';
import { Variant } from 'webapp/product/price-variant';
import { linkTitle } from 'webapp/product/product-seo-info';
import { mapContentHorizontalAlignment, mapContentVerticalAlignment } from 'webapp/shared/content-alignment';
import { white } from 'webapp/shared/sharedTheme';
import { LayoutProvider, useLayout } from 'webapp/shared/use-layout';
import { isSvenssons, lighten } from 'webapp/theme';
import { useViewEventTrigger } from 'webapp/tracking';
import { addSelectPromotionToDataLayer, addViewPromotionToDataLayer } from 'webapp/tracking/events/promotions';
import { useProductCardPlacement } from 'webapp/use-product-card-placement';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';

export default registerComponent<NordicNest.Api.ImageWithFocalPointsApiModel>(ImageWithFocalPointsBlock);

function ImageWithFocalPointsBlock(props: NordicNest.Api.ImageWithFocalPointsApiModel) {
  const context = useLayout();
  return (
    <ProgressiveHydration>
      <LayoutProvider {...context} index={(props as any).index}>
        <ImageWithFocalPoints {...props} />
      </LayoutProvider>
    </ProgressiveHydration>
  );
}

type FocalPointPropType = NordicNest.Api.ProductWithFocalPointApiModel & {
  backgroundColor?: string;
  iconColor?: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  content: NordicNest.Api.ImageWithFocalPointsApiModel | NordicNest.Api.AdApiModel;
};

export function FocalPoint(props: FocalPointPropType) {
  const [showProductCard, setShowProductCard] = React.useState(false);
  const hoverIntentTimeoutRef = React.useRef<number>(undefined);
  const { pointRef, productCardRef } = useProductCardPlacement(props.containerRef);

  const handleMouseEnter = React.useCallback(() => {
    setShowProductCard(true);
    clearTimeout(hoverIntentTimeoutRef.current);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    hoverIntentTimeoutRef.current = window.setTimeout(() => {
      setShowProductCard(false);
    }, 200);
  }, []);

  const themeDecorator = React.useMemo(
    () =>
      styled({
        backgroundColor: t => props.backgroundColor ?? t.focalPoint.iconBackgroundColor,
        color: props.iconColor ?? 'inherit',
        animation: {
          name: {
            '0%': {
              boxShadow: t => `0 0 0 0 ${lighten(props.backgroundColor ?? t.focalPoint.iconBackgroundColor, 88)}`,
            },
            '70%': {
              boxShadow: '0 0 0 15px rgb(0 0 0 / 0%)',
            },
            '100%': {
              boxShadow: '0 0 0 0 rgb(0 0 0 / 0%)',
            },
          },
          iterationCount: 'infinite',
          duration: 2000,
        },
      }),
    [props.backgroundColor, props.iconColor],
  );

  const Price = isSvenssons ? SvenssonsPrice : GeneralPrice;

  return (
    <Point
      ref={pointRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => setShowProductCard(true)}
      aria-label={linkTitle(props.product)}
      css={styled(themeDecorator, { left: props.x + '%', top: props.y + '%' })}
    >
      <Plus />
      <ProductCard
        ref={productCardRef}
        css={showProductCard && showCardDecorator}
        to={props.product.url}
        title={linkTitle(props.product)}
        onClick={() => addSelectPromotionToDataLayer({ content: props.content, items: [props.product] })}
      >
        <ProductImage image={props.product.images[0]} />
        <ProductInfo>
          <DisplayName>{props.product.productName}</DisplayName>
          {props.product.variantName}
          <Price price={props.product.price} variant={Variant.Compact} />
        </ProductInfo>
      </ProductCard>
    </Point>
  );
}

function ImageWithFocalPoints(props: NordicNest.Api.ImageWithFocalPointsApiModel) {
  const isMobile = useMatchMobile();
  const image = isMobile ? props.smallViewportImage || props.largeViewportImage : props.largeViewportImage;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const points = isMobile ? props.smallViewPoints : props.largeViewPoints;
  const products = points.filter(point => point.product).map(point => point.product);

  useViewEventTrigger(containerRef, addViewPromotionToDataLayer, {
    content: props,
    items: products,
  });

  const { hasLayoutBlockParent } = useLayout();

  const marginDecorator = React.useMemo(
    () =>
      styled(createMaxSmallViewportDecorator({ marginBottom: hasLayoutBlockParent ? 0 : t => t.spacing.l }), {
        marginBottom: hasLayoutBlockParent ? 0 : t => t.spacing.xl,
      }),
    [hasLayoutBlockParent],
  );
  const contentDecorator = React.useMemo(
    () =>
      styled({
        justifyContent: mapContentVerticalAlignment(props.contentVerticalAlignment),
        alignItems: mapContentHorizontalAlignment(props.contentHorizontalAlignment),
        textAlign: props.contentHorizontalAlignment === 'center' ? 'center' : undefined,
      }),
    [props.contentHorizontalAlignment, props.contentVerticalAlignment],
  );

  const content = (
    <ContentWrapper ref={containerRef}>
      <Media image={image} />
      <Content css={contentDecorator}>
        <Document styleDecorators={styles} decorators={decorators} elements={props.content} />
        {props.buttons?.map((button, i) => <ButtonBlock key={button.id ?? i} {...button} />)}
      </Content>
      {points.map((p, i) =>
        p.product ? (
          <FocalPoint
            key={p.product?.url + i}
            {...p}
            backgroundColor={props.backgroundColor}
            iconColor={props.iconColor}
            containerRef={containerRef}
            content={props}
          />
        ) : null,
      )}
    </ContentWrapper>
  );

  return <Base css={styled(marginDecorator, !hasLayoutBlockParent && baseDecorator)}>{content}</Base>;
}

const baseDecorator = styled(maxContentNormalWidthDecorator, desktopEdgeMarginDecorator);

const styles: StyleDecorators = {
  h1: styled(createMaxSmallViewportDecorator({ fontSize: t => t.fontSize.xl, marginBottom: 0 }), {
    fontSize: t => t.fontSize.xxxl,
    marginBottom: 0,
  }),
  h2: styled(createMaxSmallViewportDecorator({ fontSize: t => t.fontSize.l, marginBottom: 0 }), {
    fontSize: t => t.fontSize.xl,
    marginBottom: 0,
  }),
  h3: styled(createMaxSmallViewportDecorator({ fontSize: t => t.fontSize.s, marginBottom: 0 }), {
    fontSize: t => t.fontSize.l,
    marginBottom: 0,
  }),
  p: styled({
    marginBottom: t => t.spacing.xxs,
    lineHeight: 'normal',
  }),
};

const Content = styled.div(
  createMaxSmallViewportDecorator({
    padding: { x: t => t.spacing.m },
    fontSize: t => t.fontSize.xxs,
  }),
  {
    padding: { x: t => t.spacing.l, y: t => t.spacing.s },
    display: 'flex',
    flexDirection: 'column',
    color: 'white',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
);

const showCardDecorator = styled({
  opacity: 1,
  pointerEvents: 'auto',
});

const ProductCard = styled(Link, createTransitionDecorator({ property: 'opacity' }), {
  display: 'flex',
  position: 'absolute',
  bottom: '120%',
  left: 0,
  width: 200,
  backgroundColor: t => t.focalPoint.imageBackgroundColor,
  color: 'black',
  borderRadius: 6,
  opacity: 0,
  pointerEvents: 'none',
  zIndex: 1,
  overflow: 'hidden',
});

const DisplayName = styled.div({
  fontWeight: 'bold',
});

const ProductImage = styled(Image, {
  width: 50,
  height: 50,
  borderTopLeftRadius: 6,
  borderBottomLeftRadius: 6,
  margin: { top: t => t.sharedSpacing.xs },
});

const ProductInfo = styled.div({
  padding: { xy: t => t.sharedSpacing.xs },
  width: 150,
  backgroundColor: white,
  textAlign: 'left', //Needed to ensure left aligned text when used in ads where text can be centered
});

const Base = styled(Block, {
  position: 'relative',
  overflow: 'hidden',
});

const Media = styled(Image, {
  width: '100%',
});

const ContentWrapper = styled.div({
  position: 'relative',
});

const Point = styled.div({
  borderRadius: '50%',
  cursor: 'pointer',
  width: 30,
  height: 30,
  fontSize: t => t.fontSize.xxxs,
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});
